import React, { memo, useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import { DropdownItem } from '@atlaskit/dropdown-menu';
import CheckIcon from '@atlaskit/icon/core/migration/check-mark--check';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import type { SetAsDefaultItem_container$key } from '@atlassian/jira-relay/src/__generated__/SetAsDefaultItem_container.graphql';
import type { SetAsDefaultItem_tab$key } from '@atlassian/jira-relay/src/__generated__/SetAsDefaultItem_tab.graphql';
import { useSetAsDefault } from '../services/set-as-default/useSetAsDefault.tsx';
import messages from './messages.tsx';

type Props = {
	tabFragment: SetAsDefaultItem_tab$key;
	containerFragment: SetAsDefaultItem_container$key;
	onClick?: () => void;
};

export const SetAsDefaultItem = memo(({ tabFragment, containerFragment, onClick }: Props) => {
	const { formatMessage } = useIntl();
	const { setAsDefault } = useSetAsDefault();

	const { id, __id, isDefault } = useFragment(
		graphql`
			fragment SetAsDefaultItem_tab on JiraNavigationItem {
				id @required(action: THROW)
				__id @required(action: THROW)
				isDefault @required(action: THROW)
			}
		`,
		tabFragment,
	);
	const containerData = useFragment(
		graphql`
			fragment SetAsDefaultItem_container on JiraContainerNavigationResult {
				... on JiraContainerNavigation {
					id
					scopeId
				}
			}
		`,
		containerFragment,
	);

	const handleClick = useCallback(() => {
		if (containerData.id && !isDefault) {
			setAsDefault({
				id,
				__id,
				containerNavigationId: containerData.id,
				scopeId: containerData?.scopeId,
			});
			onClick?.();
		}
	}, [isDefault, setAsDefault, id, __id, containerData?.id, containerData?.scopeId, onClick]);

	return (
		<DropdownItem
			onClick={handleClick}
			elemAfter={
				isDefault && (
					<CheckIcon
						spacing="spacious"
						label={formatMessage(messages.selected)}
						color={token('color.icon.selected')}
					/>
				)
			}
		>
			{formatMessage(messages.setAsDefaultView)}
		</DropdownItem>
	);
});
